import { Suspense, useEffect, useState } from 'react'
import { lazyWithReload as lazy } from '@/utils/lazyWithReload'
import { AnimatePresence, motion } from 'framer-motion'
import { BlurFade } from '@/components/ui/blur-fade'
import { cn } from '@/lib/utils'
import { PillButton } from './PillButton'

// The WebGL orb (ogl) is decorative, so it loads in its own chunk after the
// hero has painted instead of sitting in the entry bundle.
const Orb = lazy(() => import('./Orb'))

// Fixed positions ("slots"). Images circulate through these slots.
const HERO_COLLAGE_SLOTS = [
  'absolute left-0 top-4 h-44 w-40 rotate-[-4deg] shadow-lg sm:h-52 sm:w-48',
  'absolute right-0 top-0 h-52 w-52 rotate-[3deg] shadow-xl sm:h-64 sm:w-64',
  'absolute bottom-0 left-12 h-44 w-56 rotate-[2deg] shadow-lg sm:h-52 sm:w-72',
]

const HERO_COLLAGE_IMAGES = [
  { alt: 'Hands in mudra (close-up)', src: './home-page/1.jpg' },
  { alt: 'Group circle in low light', src: './home-page/2.jpg' },
  { alt: 'Facilitator mid-instruction', src: './home-page/3.jpg' },
]

const ROTATE_INTERVAL_MS = 3500

function HeroCollage() {
  // const reduce = useReducedMotion()
  const reduce = false
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (reduce) return
    const id = setInterval(
      () => setOffset((o) => (o + 1) % HERO_COLLAGE_IMAGES.length),
      ROTATE_INTERVAL_MS
    )
    return () => clearInterval(id)
  }, [reduce])

  return (
    <div className="relative h-[360px] sm:h-[440px]">
      {HERO_COLLAGE_SLOTS.map((slotClassName, slotIndex) => {
        const image =
          HERO_COLLAGE_IMAGES[
            (slotIndex + offset) % HERO_COLLAGE_IMAGES.length
          ]
        return (
          <div key={slotIndex} className={cn('overflow-hidden rounded-2xl', slotClassName)}>
            <AnimatePresence initial={false}>
              <motion.img
                key={image.src}
                src={image.src}
                alt={image.alt}
                loading="eager"
                decoding="async"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2.5, ease: 'easeInOut' }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

export function HeroSection() {
  return (
    <section className="bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="flex flex-col justify-center mx-auto lg:min-h-[75vh] max-w-[1200px] px-6 py-16 lg:px-20 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-[55fr_45fr] lg:items-center">
   
          {/* Text — Orb sits behind as an absolute background layer */}
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 -left-16 -top-16 h-[120%] w-[120%] opacity-40">
              <Suspense fallback={null}>
                <Orb
                  hue={0}
                  hoverIntensity={0.25}
                  rotateOnHover={true}
                  forceHoverState={false}
                  backgroundColor="#000000"
                />
              </Suspense>
            </div>

            <div className="relative z-10">
              <BlurFade>
                <span className="inline-block rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
                  A curated wellness platform
                </span>
              </BlurFade>

              <BlurFade delay={0.1}>
                <h1 className="mt-6 text-4xl font-bold leading-[1.1] text-gray-900 sm:text-5xl lg:text-6xl">
                  Your Wellness Loop
                  <br />
                  starts here.
                </h1>
              </BlurFade>

              <BlurFade delay={0.2}>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-700">
                  Discover trusted events, classes, programs, and everyday
                  practices that support your body, mind, emotions, and sense of
                  connection.
                </p>
              </BlurFade>

              <BlurFade delay={0.3}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <PillButton to="/events" variant="primary">
                    Explore experiences →
                  </PillButton>
                  {/* TODO: replace with real WhatsApp community invite link */}
                  <PillButton href="./onboarding" variant="secondary">
                    Join the community
                  </PillButton>
                </div>
              </BlurFade>

              <BlurFade delay={0.4}>
                <div className="mt-0 border-primary-100 pt-5 text-base text-gray-600">
                  Trusted by 100+ facilitators · 5,000+ seekers
                </div>
              </BlurFade>
            </div>
          </div>

          {/* Image collage */}
          <BlurFade delay={0.2}>
            <HeroCollage />
          </BlurFade>
        </div>
      </div>
    </section>
  )
}
